import { Controller } from "@hotwired/stimulus"
// three.js (~150KB) is loaded lazily in boot() ONLY after the mobile/reduced-
// motion/WebGL2 gates pass, so mobile — where this hero always falls back to the
// CSS gradient — never downloads it. WebGL.js is a standalone ~1KB capability
// check with no dependency on three's core, so it stays a static import: we need
// it synchronously to gate BEFORE deciding whether to fetch three at all.
import WebGL from "three/addons/capabilities/WebGL.js"

// "Quiet Storage Grid" hero (Concept A).
//
// A single InstancedMesh of rounded "unit doors" laid out in a ~12x8 grid, lit
// by an indigo hemisphere light and a teal-tinted directional light. A handful
// of doors are dithered teal ("occupied") among the deep-indigo ones. The only
// motion is a barely-perceptible camera parallax eased toward the pointer — no
// per-frame allocations, near-zero cost. It sits LOW and DIM behind the hero
// product frame and must never overpower the headline.
//
// This controller is defensive by design: it gates on WebGL2 BEFORE building a
// renderer, skips WebGL entirely on small screens / reduced-motion, renders a
// single static frame when motion is disabled, and fully tears everything down
// on disconnect() so a Turbo navigation never leaks a WebGL context (the
// dreaded "Too many active WebGL contexts" warning). On any failure it silently
// reveals the CSS gradient fallback — no throws, no console spam.
export default class extends Controller {
  static targets = ["canvas", "fallback"]

  connect() {
    this.running = false
    this.frameId = null
    this.disposables = []

    // Mobile (<=768px): skip WebGL for load speed + battery. The section's
    // radial gradient + product image already look intentional.
    const isMobile = window.matchMedia("(max-width: 768px)").matches
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    if (isMobile || !this.hasCanvasTarget) {
      this.showFallback()
      return
    }

    // Gate on WebGL2 availability BEFORE fetching three or constructing a
    // renderer.
    if (!WebGL.isWebGL2Available()) {
      this.showFallback()
      return
    }

    this.boot()
  }

  // Lazily fetch three.js (and RoundedBoxGeometry, which pulls in three's core)
  // only now that every gate has passed, then build the scene. Everything that
  // depends on the scene existing — the render loop and listener wiring — lives
  // here so it can't run before three has loaded.
  async boot() {
    let THREE, RoundedBoxGeometryModule
    try {
      [THREE, RoundedBoxGeometryModule] = await Promise.all([
        import("three"),
        import("three/addons/geometries/RoundedBoxGeometry.js")
      ])
    } catch (e) {
      this.showFallback()
      return
    }

    // A fast Turbo navigation can disconnect the controller mid-fetch — bail
    // rather than build a scene into a detached element (and leak a context).
    if (!this.element.isConnected || !this.hasCanvasTarget) return

    this.THREE = THREE
    this.RoundedBoxGeometry = RoundedBoxGeometryModule.RoundedBoxGeometry

    try {
      this.initScene()
    } catch (e) {
      // Any failure (e.g. major performance caveat, context creation) -> fall
      // back silently. Clean up anything partially built.
      this.teardown()
      this.showFallback()
      return
    }

    // Pause when off-screen and when the tab is hidden.
    this.onVisibility = () => this.updateRunning()
    document.addEventListener("visibilitychange", this.onVisibility)

    this.observer = new IntersectionObserver((entries) => {
      this.intersecting = entries.some((e) => e.isIntersecting)
      this.updateRunning()
    }, { threshold: 0.01 })
    this.observer.observe(this.element)
    this.intersecting = true

    this.onResize = () => this.handleResize()
    window.addEventListener("resize", this.onResize, { passive: true })

    this.onPointerMove = (e) => this.handlePointer(e)
    window.addEventListener("pointermove", this.onPointerMove, { passive: true })

    if (this.reducedMotion) {
      // One static frame, no loop.
      this.renderer.render(this.scene, this.camera)
    } else {
      this.updateRunning()
    }
  }

  disconnect() {
    this.teardown()
  }

  // --- Scene construction ---------------------------------------------------

  initScene() {
    // Aliased from the lazily-imported modules so the rest of this method reads
    // as if three were a static top-level import.
    const THREE = this.THREE
    const RoundedBoxGeometry = this.RoundedBoxGeometry
    const canvas = this.canvasTarget
    const width = canvas.clientWidth || this.element.clientWidth || 800
    const height = canvas.clientHeight || this.element.clientHeight || 600

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      powerPreference: "low-power",
      failIfMajorPerformanceCaveat: true
    })
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
    this.renderer.setSize(width, height, false)

    // Guard against context loss noise (e.g. GPU reset on a laptop sleep).
    this.onContextLost = (event) => {
      event.preventDefault()
      this.stop()
    }
    canvas.addEventListener("webglcontextlost", this.onContextLost, false)

    this.scene = new THREE.Scene()

    this.camera = new THREE.PerspectiveCamera(40, width / height, 0.1, 100)
    this.cameraBase = new THREE.Vector3(0, 6.4, 12.5)
    this.camera.position.copy(this.cameraBase)
    this.camera.lookAt(0, 0, 0)

    // Lights: indigo hemisphere fill + one teal-tinted directional key.
    const hemi = new THREE.HemisphereLight(0x6366f1, 0x1e1b4b, 1.05)
    this.scene.add(hemi)
    const key = new THREE.DirectionalLight(0x2dd4bf, 1.15)
    key.position.set(-6, 10, 8)
    this.scene.add(key)
    const ambient = new THREE.AmbientLight(0x312e81, 0.55)
    this.scene.add(ambient)

    // The grid of "unit doors".
    const cols = 12
    const rows = 8
    const count = cols * rows
    const gap = 1.18
    const doorW = 1.0
    const doorH = 1.0
    const doorD = 0.42

    this.geometry = new RoundedBoxGeometry(doorW, doorH, doorD, 3, 0.12)
    this.material = new THREE.MeshStandardMaterial({
      roughness: 0.62,
      metalness: 0.15
    })
    this.disposables.push(this.geometry, this.material)

    this.mesh = new THREE.InstancedMesh(this.geometry, this.material, count)
    this.mesh.instanceMatrix.setUsage(THREE.StaticDrawUsage)

    const indigo = new THREE.Color(0x4338ca)
    const teal = new THREE.Color(0x14b8a6)
    const dummy = new THREE.Object3D()

    // Deterministic "occupied" dithering — a sparse, pleasant scatter of teal.
    const occupied = new Set([5, 13, 21, 28, 34, 47, 52, 63, 70, 81, 88, 92])

    let i = 0
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        dummy.position.set(
          (c - (cols - 1) / 2) * gap,
          (r - (rows - 1) / 2) * gap,
          0
        )
        dummy.updateMatrix()
        this.mesh.setMatrixAt(i, dummy.matrix)
        this.mesh.setColorAt(i, occupied.has(i) ? teal : indigo)
        i++
      }
    }
    this.mesh.instanceMatrix.needsUpdate = true
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true

    // Tilt the whole grid back for a low, isometric-ish read.
    this.mesh.rotation.x = -0.32
    this.mesh.rotation.z = 0.04
    this.scene.add(this.mesh)

    // Scratch state for the parallax (no per-frame allocations).
    this.pointer = new THREE.Vector2(0, 0)
    this.pointerTarget = new THREE.Vector2(0, 0)
  }

  // --- Animation ------------------------------------------------------------

  start() {
    if (this.running || this.reducedMotion || !this.renderer) return
    this.running = true
    this.tick = this.tick.bind(this)
    this.frameId = requestAnimationFrame(this.tick)
  }

  stop() {
    this.running = false
    if (this.frameId !== null) {
      cancelAnimationFrame(this.frameId)
      this.frameId = null
    }
  }

  updateRunning() {
    const visible = document.visibilityState !== "hidden"
    if (visible && this.intersecting && !this.reducedMotion) {
      this.start()
    } else {
      this.stop()
    }
  }

  tick() {
    if (!this.running) return

    // Ease pointer toward target; map to a tiny camera offset.
    this.pointer.x += (this.pointerTarget.x - this.pointer.x) * 0.045
    this.pointer.y += (this.pointerTarget.y - this.pointer.y) * 0.045

    this.camera.position.x = this.cameraBase.x + this.pointer.x * 1.1
    this.camera.position.y = this.cameraBase.y + this.pointer.y * 0.7
    this.camera.lookAt(0, 0, 0)

    this.renderer.render(this.scene, this.camera)
    this.frameId = requestAnimationFrame(this.tick)
  }

  // --- Events ---------------------------------------------------------------

  handlePointer(e) {
    // Normalize to [-1, 1] across the viewport.
    this.pointerTarget.x = (e.clientX / window.innerWidth) * 2 - 1
    this.pointerTarget.y = -((e.clientY / window.innerHeight) * 2 - 1)
  }

  handleResize() {
    if (!this.renderer || !this.camera) return
    const canvas = this.canvasTarget
    const width = canvas.clientWidth || this.element.clientWidth
    const height = canvas.clientHeight || this.element.clientHeight
    if (!width || !height) return
    this.camera.aspect = width / height
    this.camera.updateProjectionMatrix()
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
    this.renderer.setSize(width, height, false)
    if (this.reducedMotion) this.renderer.render(this.scene, this.camera)
  }

  // --- Fallback + teardown --------------------------------------------------

  showFallback() {
    if (this.hasCanvasTarget) this.canvasTarget.style.display = "none"
    if (this.hasFallbackTarget) this.fallbackTarget.removeAttribute("hidden")
  }

  teardown() {
    this.stop()

    if (this.onVisibility) {
      document.removeEventListener("visibilitychange", this.onVisibility)
      this.onVisibility = null
    }
    if (this.onResize) {
      window.removeEventListener("resize", this.onResize)
      this.onResize = null
    }
    if (this.onPointerMove) {
      window.removeEventListener("pointermove", this.onPointerMove)
      this.onPointerMove = null
    }
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    if (this.onContextLost && this.hasCanvasTarget) {
      this.canvasTarget.removeEventListener("webglcontextlost", this.onContextLost)
      this.onContextLost = null
    }

    if (this.mesh) {
      this.scene?.remove(this.mesh)
      this.mesh.dispose()
      this.mesh = null
    }
    if (this.disposables) {
      this.disposables.forEach((d) => d?.dispose && d.dispose())
      this.disposables = []
    }
    if (this.renderer) {
      this.renderer.dispose()
      this.renderer.forceContextLoss()
      this.renderer = null
    }
    this.scene = null
    this.camera = null
  }
}
